/**
 * AdminVoiceCoverage.jsx
 *
 * "Your Voice Across Reclaim" — programme-level coverage dashboard.
 *
 * Where /admin/audio shows individual file slots, this lens groups
 * everything into PROGRAMMES (Resets, Rituals, Presence Programme,
 * each Rewrite Engine domain, etc.) so Michael always knows at a
 * glance which programme is the highest-emotional-payoff next pass to
 * record.
 *
 * Route: /admin/voice-coverage  (admin only)
 * Backend: GET /api/admin/voice-coverage
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "../AdminPages";
import {
  ArrowLeft, ChevronDown, ChevronRight, Mic, Sparkles, AlertCircle,
  CheckCircle2, Circle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

const PRIO_STYLES = {
  P0: { ring: "ring-amber-300", dotBg: "bg-amber-500",  label: "P0 · Launch blocker" },
  P1: { ring: "ring-stone-300", dotBg: "bg-stone-500",  label: "P1 · Premium polish" },
  P2: { ring: "ring-stone-200", dotBg: "bg-stone-400",  label: "P2 · Library depth" },
};

const VOICED_BY_STYLES = {
  human: {
    label: "In Michael's voice",
    bgClass: "bg-[#A88A4C]",
    iconColor: "text-[#A88A4C]",
    Icon: CheckCircle2,
  },
  tts: {
    label: "TTS fallback",
    bgClass: "bg-stone-400",
    iconColor: "text-stone-400",
    Icon: Mic,
  },
  none: {
    label: "Not voiced yet",
    bgClass: "bg-stone-200",
    iconColor: "text-stone-300",
    Icon: Circle,
  },
};

function CoverageBar({ counts }) {
  // Three-segment bar: human → tts → none. Always renders the full width
  // so two programmes side-by-side are visually comparable.
  const total = Math.max(1, counts.total || 0);
  const h = (counts.human / total) * 100;
  const t = (counts.tts / total) * 100;
  // (none is the visual remainder)
  return (
    <div className="w-full h-2.5 rounded-full overflow-hidden flex bg-stone-200">
      <div
        className="h-full transition-all duration-700"
        style={{ width: `${h}%`, background: "#A88A4C" }}
      />
      <div
        className="h-full transition-all duration-700"
        style={{ width: `${t}%`, background: "rgb(168, 162, 158)" }}
      />
    </div>
  );
}

function ProgrammeRow({ p, open, onToggle }) {
  const human_pct = p.coverage.human_pct;
  const voiced_pct = p.coverage.human_pct + p.coverage.tts_pct;
  const prio = PRIO_STYLES[p.priority] || PRIO_STYLES.P2;

  return (
    <div
      className={`rounded-2xl bg-white shadow-sm ring-1 ${prio.ring} transition-all duration-300`}
      data-testid={`vc-programme-${p.id}`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-5 flex items-center gap-4 hover:bg-stone-50/60 rounded-2xl transition-colors"
        data-testid={`vc-programme-${p.id}-toggle`}
      >
        <span className={`w-2 h-2 rounded-full ${prio.dotBg}`} aria-hidden />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3 mb-1">
            <h3 className="font-semibold text-[#292524]" style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "1.25rem", letterSpacing: "-0.01em" }}>
              {p.label}
            </h3>
            <span className="text-[10px] uppercase tracking-[0.2em] text-stone-400 font-medium">
              {p.counts.human} of {p.counts.total} voiced
            </span>
          </div>
          <p className="text-xs text-stone-500 mb-3 italic" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
            {p.subtitle}
          </p>
          <div className="flex items-center gap-4">
            <CoverageBar counts={p.counts} />
            <span className="text-sm font-semibold tabular-nums" style={{ color: "#A88A4C", minWidth: "3rem", textAlign: "right" }}>
              {human_pct}%
            </span>
          </div>
          {voiced_pct > human_pct && (
            <p className="text-[10px] uppercase tracking-[0.2em] text-stone-400 mt-2">
              + {p.coverage.tts_pct}% via TTS fallback
            </p>
          )}
        </div>
        {open ? <ChevronDown size={18} className="text-stone-400" /> : <ChevronRight size={18} className="text-stone-400" />}
      </button>

      {open && (
        <div className="px-5 pb-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-2 pl-5 border-l border-stone-100">
            {p.tracks.map((t) => {
              const v = VOICED_BY_STYLES[t.voiced_by] || VOICED_BY_STYLES.none;
              const Icon = v.Icon;
              return (
                <div
                  key={t.id}
                  className="flex items-center gap-2.5 py-1"
                  data-testid={`vc-track-${t.id}`}
                  title={`${v.label} · ${t.path}`}
                >
                  <Icon size={12} className={v.iconColor} />
                  <span className={t.voiced_by === "human" ? "text-stone-700 text-sm" : "text-stone-400 text-sm"}>
                    {t.label}
                  </span>
                  {t.voiced_by === "human" && t.size_kb > 0 && (
                    <span className="text-[10px] text-stone-300 ml-auto tabular-nums">
                      {t.size_kb} KB
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminVoiceCoverage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/voice-coverage`, { headers: auth() });
        if (!r.ok) throw new Error("fetch_failed");
        const d = await r.json();
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError("Couldn't load voice coverage. Make sure you're signed in as admin.");
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-voice-coverage">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900 transition-colors"
            data-testid="vc-back"
          >
            <ArrowLeft size={16} /> Admin
          </button>
          <button
            onClick={() => navigate("/admin/audio")}
            className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-stone-900 transition-colors"
            data-testid="vc-to-audio"
          >
            Audio Files →
          </button>
        </div>

        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.3em] text-[#A88A4C] font-medium">
            Your Voice Across Reclaim
          </p>
          <h1
            className="italic text-[#292524]"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(1.8rem, 3.4vw, 2.4rem)",
              fontWeight: 400,
              lineHeight: 1.15,
            }}
          >
            Where your voice already lives.
          </h1>
          <p className="text-sm text-stone-500 italic" style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "1.05rem" }}>
            Programme-by-programme coverage of every track recorded in your voice today.
          </p>
        </div>

        {loading && (
          <div className="py-12 text-center text-stone-400 italic" style={{ fontFamily: '"Cormorant Garamond", serif' }} data-testid="vc-loading">
            Reading the library…
          </div>
        )}

        {error && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 text-red-700 ring-1 ring-red-100" data-testid="vc-error">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {data && !loading && (
          <>
            {/* Headline number */}
            <div
              className="relative overflow-hidden rounded-3xl p-7"
              style={{
                background: "linear-gradient(135deg, #1A1108 0%, #0F0A05 55%, #060403 100%)",
                boxShadow: "0 24px 64px -28px rgba(215,187,122,0.45)",
              }}
              data-testid="vc-overall"
            >
              <div aria-hidden className="absolute inset-0 pointer-events-none"
                   style={{ background: "radial-gradient(circle at 80% 30%, rgba(215,187,122,0.32) 0%, transparent 60%)" }} />
              <div className="relative flex flex-wrap items-end justify-between gap-6">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.4em] text-[#D7BB7A] mb-2 opacity-80">
                    Coverage Today
                  </p>
                  <p className="font-light text-white" style={{ fontSize: "clamp(3rem, 8vw, 5rem)", lineHeight: 1, fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
                    {data.overall.human_pct}<span className="text-[#D7BB7A] text-3xl ml-2">%</span>
                  </p>
                  <p className="text-stone-400 text-sm mt-2 italic" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                    {data.overall.counts.human} of {data.overall.counts.total} tracks are in your voice
                  </p>
                </div>
                <div className="flex items-center gap-6 text-xs">
                  <div className="text-center">
                    <div className="w-10 h-10 rounded-full mb-2 mx-auto flex items-center justify-center" style={{ background: "rgba(215,187,122,0.18)", border: "1px solid #A88A4C" }}>
                      <CheckCircle2 size={16} className="text-[#D7BB7A]" />
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-stone-300">In voice</p>
                    <p className="text-white text-base font-semibold tabular-nums mt-0.5">{data.overall.counts.human}</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 rounded-full mb-2 mx-auto flex items-center justify-center" style={{ background: "rgba(168,162,158,0.20)", border: "1px solid rgba(168,162,158,0.45)" }}>
                      <Mic size={14} className="text-stone-300" />
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-stone-300">TTS</p>
                    <p className="text-white text-base font-semibold tabular-nums mt-0.5">{data.overall.counts.tts}</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 rounded-full mb-2 mx-auto flex items-center justify-center" style={{ background: "rgba(120,113,108,0.18)", border: "1px solid rgba(120,113,108,0.30)" }}>
                      <Circle size={14} className="text-stone-400" />
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-stone-300">To record</p>
                    <p className="text-white text-base font-semibold tabular-nums mt-0.5">{data.overall.counts.none}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Programmes list */}
            <div className="space-y-3 mt-2">
              {data.programmes.map((p) => (
                <ProgrammeRow
                  key={p.id}
                  p={p}
                  open={openId === p.id}
                  onToggle={() => setOpenId((id) => (id === p.id ? null : p.id))}
                />
              ))}
            </div>

            <div className="mt-8 p-5 rounded-2xl bg-stone-50 ring-1 ring-stone-100 flex items-start gap-3">
              <Sparkles size={16} className="text-[#A88A4C] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-stone-700 font-medium mb-1">Where to record next</p>
                <p className="text-xs text-stone-500 italic leading-relaxed" style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "0.95rem" }}>
                  The programmes with the largest gap between expected and voiced tracks are the highest-payoff
                  next sessions. The scripts are waiting in /app/backend/static/audio/voice/OUTSTANDING_SCRIPTS.pdf.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
